import React, { forwardRef, useState, useMemo, useId, useRef } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  size as sizeMiddleware,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  useClick,
  useMergeRefs,
  useListNavigation,
  useTypeahead,
  type Placement,
} from '@floating-ui/react';
import { InputContainer, InputLabel, InputError, SINGLE_LINE_FIELD_CLASS } from './InputBase';
import { Check, ChevronDown } from 'lucide-react';
import {
  getSelectorOptionStateClass,
  SELECTOR_FLYOUT_CHROME_CLASS,
  SELECTOR_OPTION_CLASS,
} from './selectorPresentation';

export interface SelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  divider?: boolean;
}

export interface SelectProps extends Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  'size' | 'onChange' | 'value'
> {
  label?: string;
  id?: string;
  options: SelectOption[];
  error?: string;
  containerClassName?: string;
  size?: 'md' | 'sm';
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  placement?: Placement;
}

export const Select = forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      label,
      options,
      error,
      className = '',
      containerClassName,
      size = 'md',
      value,
      onChange,
      disabled,
      placeholder = 'Select...',
      placement = 'bottom-start',
      id,
      name,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const generatedId = useId();
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const listRef = useRef<Array<HTMLElement | null>>([]);
    const labelsRef = useRef<Array<string | null>>([]);
    labelsRef.current = options.map((option) => option.label);
    const selectedIndex = options.findIndex((option) => option.value === value);
    const controlId = `${id ?? generatedId}-control`;
    const labelId = `${id ?? generatedId}-label`;
    const controlRef = useRef<HTMLDivElement>(null);

    const { refs, floatingStyles, context } = useFloating({
      open: isOpen,
      onOpenChange: setIsOpen,
      whileElementsMounted: autoUpdate,
      placement,
      middleware: [
        offset(5),
        flip({ padding: 5 }),
        shift({ padding: 5 }),
        sizeMiddleware({
          apply({ rects, elements, availableHeight }) {
            Object.assign(elements.floating.style, {
              maxHeight: `min(${availableHeight - 10}px, var(--spacing-80, 320px))`,
              width: `${rects.reference.width}px`,
            });
          },
        }),
      ],
    });

    const mergedRef = useMergeRefs([refs.setReference, ref, controlRef]);

    const click = useClick(context, { enabled: !disabled });
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'listbox' });
    const navigation = useListNavigation(context, {
      listRef,
      activeIndex,
      selectedIndex: selectedIndex < 0 ? null : selectedIndex,
      onNavigate: setActiveIndex,
      virtual: true,
      loop: true,
      enabled: !disabled,
    });
    const typeahead = useTypeahead(context, {
      listRef: labelsRef,
      activeIndex,
      onMatch: setActiveIndex,
      enabled: isOpen && !disabled,
    });
    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
      click,
      dismiss,
      role,
      navigation,
      typeahead,
    ]);

    const selectedOption = useMemo(() => options.find((o) => o.value === value), [options, value]);

    const handleSelect = (newValue: string) => {
      onChange?.(newValue);
      setIsOpen(false);
    };
    return (
      <InputContainer className={containerClassName}>
        {label && (
          <InputLabel
            id={labelId}
            label={label}
            htmlFor={controlId}
            onClick={() => {
              if (!disabled) controlRef.current?.focus();
            }}
          />
        )}

        <div className="relative">
          <input
            type="hidden"
            name={name}
            id={id}
            value={value || ''}
            onChange={() => {}} // Controlled by parent
          />
          <div
            ref={mergedRef}
            className={[
              SINGLE_LINE_FIELD_CLASS,
              'flex w-full cursor-pointer items-center justify-between text-sm transition-all duration-fast',
              size === 'sm' ? 'h-9 px-3' : 'h-input-height px-4',
              error ? 'border-glyph-critical' : '',
              disabled
                ? 'cursor-not-allowed border-outline-default/40 bg-input/60 text-glyph-primary/60'
                : '',
              className,
            ]
              .filter(Boolean)
              .join(' ')}
            {...getReferenceProps({
              ...(props as unknown as React.HTMLProps<HTMLDivElement>),
              onKeyDown(event) {
                props.onKeyDown?.(event as unknown as React.KeyboardEvent<HTMLSelectElement>);
                if (disabled || event.defaultPrevented) return;
                if (isOpen && (event.key === 'Enter' || event.key === ' ')) {
                  const index = activeIndex ?? (selectedIndex < 0 ? null : selectedIndex);
                  if (index !== null && options[index]) {
                    event.preventDefault();
                    handleSelect(options[index].value);
                  }
                }
                if (event.key === 'Tab') setIsOpen(false);
              },
            })}
            id={controlId}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy ?? (!ariaLabel && label ? labelId : undefined)}
            aria-disabled={disabled || undefined}
            tabIndex={disabled ? -1 : 0}
          >
            <span className="truncate">
              {selectedOption ? (
                selectedOption.label
              ) : (
                <span className="text-glyph-disabled/60">{placeholder}</span>
              )}
            </span>
            <div
              className={[
                'pointer-events-none flex-shrink-0 text-glyph-secondary',
                'transition-transform duration-fast ease-smooth',
                isOpen ? 'rotate-180' : '',
              ].join(' ')}
              aria-hidden="true"
            >
              <ChevronDown size={size === 'sm' ? 14 : 16} strokeWidth={2.5} />
            </div>
          </div>

          <FloatingPortal>
            {isOpen && !disabled && (
              <div
                ref={refs.setFloating}
                style={{
                  ...floatingStyles,
                  zIndex: 'var(--z-menu)',
                }}
                {...getFloatingProps()}
              >
                <div
                  className={`animate-xp-flyout-enter ${SELECTOR_FLYOUT_CHROME_CLASS} flex max-h-80 flex-col gap-1 overflow-y-auto`}
                >
                  {options.length > 0 ? (
                    options.map((option, index) => (
                      <React.Fragment key={option.value}>
                        {option.divider && index > 0 && (
                          <div className="mx-2 my-1 h-px bg-outline-default/40" role="separator" />
                        )}
                        <div
                          id={`${controlId}-option-${index}`}
                          ref={(node) => {
                            listRef.current[index] = node;
                          }}
                          role="option"
                          aria-selected={option.value === value}
                          className={[
                            SELECTOR_OPTION_CLASS,
                            getSelectorOptionStateClass({
                              selected: option.value === value,
                              highlighted: activeIndex === index,
                            }),
                          ].join(' ')}
                          {...getItemProps({
                            onClick: () => handleSelect(option.value),
                          })}
                        >
                          <div className="flex min-w-0 flex-1 items-center gap-2.5">
                            <span className="min-w-0 break-words whitespace-normal">
                              {option.label}
                            </span>
                            {option.icon && (
                              <span className="shrink-0 text-glyph-tertiary">{option.icon}</span>
                            )}
                          </div>
                          {option.value === value && (
                            <Check className="h-4 w-4 shrink-0 text-accent-primary" />
                          )}
                        </div>
                      </React.Fragment>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-glyph-secondary italic">No options</div>
                  )}
                </div>
              </div>
            )}
          </FloatingPortal>
        </div>
        <InputError error={error} />
      </InputContainer>
    );
  }
);

Select.displayName = 'Select';
